import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';

interface ClimbSavedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The fields worth having but not worth holding a save back for. */
  children: React.ReactNode;
  saving: boolean;
  /** Held back while one of the fields is half-answered — see nameUnusable. */
  disabled?: boolean;
  /** Writes what was filled in here and closes. */
  onSave: () => void;
  /** Writes it, then opens an empty editor for the next climb. */
  onSaveAndNext: () => void;
  /** What the "next one" is, which depends on where the editor was opened from. */
  nextLabel: string;
}

/**
 * What is asked *after* the climb exists.
 *
 * A setter puts up a wall of problems in an afternoon, and everything that is
 * not needed to identify a climb — its description, who set it, which sector it
 * is on — is a field between them and the next one. So the save happens on the
 * few things that are required, and the rest is offered once the climb is
 * already up: filled in now, or left for whoever gets to it later.
 *
 * Both buttons write first. Someone who typed a description and then pressed
 * "the next one" meant to keep it, and losing it would be the dialog's fault
 * rather than theirs. Dismissing it keeps the climb and drops only what was
 * typed here, which is the one reading of "close" that is not a save.
 */
export function ClimbSavedDialog({
  open,
  onOpenChange,
  children,
  saving,
  disabled = false,
  onSave,
  onSaveAndNext,
  nextLabel,
}: ClimbSavedDialogProps) {
  const { t } = useTranslation();
  const held = saving || disabled;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Bounded to the screen and laid out as a column, because the fields
          below add up to more than a phone is tall: without this the dialog
          grows past the viewport and the buttons at its foot cannot be
          reached. */}
      <DialogContent className="flex max-h-[85dvh] flex-col sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('climbEditor.saved.title')}</DialogTitle>
          <DialogDescription>{t('climbEditor.saved.intro')}</DialogDescription>
        </DialogHeader>

        {/* The fields are what scrolls; the heading and the two buttons stay
            put. `min-h-0` is what lets this shrink inside the column — a flex
            child refuses to go below its content without it, which is the whole
            reason the overflow would never engage. */}
        <div className="-mx-1 min-h-0 flex-1 space-y-4 overflow-y-auto px-1">{children}</div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="button" variant="outline" className="flex-1" onClick={onSave} disabled={held}>
            {saving && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
            {t('common.save')}
          </Button>
          <Button type="button" className="flex-1" onClick={onSaveAndNext} disabled={held}>
            {nextLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
